import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback } from 'react';

import { useRecordingAnalytics } from '../../../hooks';
import { selectCaptionSettings } from '../../../selector';
import { CAPTION_FONT_OPTIONS } from '../../../utils';
import { CaretLeft } from '../../icons/caret-left';
import { CaretRight } from '../../icons/caret-right';

type FontStyleSelectorProps = {
  isExpanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  disabled?: boolean;
};

export const FontStyleSelector = ({
  isExpanded,
  onExpandChange,
  disabled = false,
}: FontStyleSelectorProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const { captions_style } = useAppSelector(selectCaptionSettings);

  const selectedFont =
    CAPTION_FONT_OPTIONS.find(f => f.id === captions_style) ||
    CAPTION_FONT_OPTIONS[0];

  const handleFontSelect = useCallback(
    (fontId: string) => {
      dispatch({
        type: '[recording] captions font style changed',
        payload: { fontStyle: fontId },
      });
      const font = CAPTION_FONT_OPTIONS.find(f => f.id === fontId);
      trackRecordingEvent('ch_creator_lab_appearance_font_selected', {
        font: font?.name ?? fontId,
      });
      onExpandChange(false);
    },
    [dispatch, onExpandChange, trackRecordingEvent],
  );

  const handleOpenList = useCallback(() => {
    onExpandChange(true);
  }, [onExpandChange]);

  const handleCloseList = useCallback(() => {
    onExpandChange(false);
  }, [onExpandChange]);

  if (isExpanded) {
    return (
      <div className="flex flex-col gap-3 h-full">
        {/* Header with back button */}
        <button
          type="button"
          onClick={handleCloseList}
          disabled={disabled}
          className="flex items-center gap-2 text-left"
        >
          <CaretLeft className="size-4 text-white" />
          <p className="text-[14px] font-medium text-white leading-[20px] m-0">
            {t('content-recording.appearance.captions.font-style.label')}
          </p>
        </button>

        {/* Font list */}
        <div className="flex flex-col gap-2 flex-1 overflow-y-auto">
          {CAPTION_FONT_OPTIONS.map(font => (
            <button
              key={font.id}
              type="button"
              onClick={() => handleFontSelect(font.id)}
              disabled={disabled}
              className={cx(
                'w-full py-3 px-4 rounded-[8px] text-center transition-colors hover:bg-spring-neutral-b0/10',
                {
                  'bg-spring-neutral-b4': captions_style === font.id,
                },
              )}
              style={{
                fontFamily: font.fontFamily,
                fontWeight: font.fontWeight,
              }}
            >
              <span className="text-[20px] text-spring-neutral-b3">
                {font.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[14px] font-medium text-white leading-[20px] tracking-[0px] m-0">
        {t('content-recording.appearance.captions.font-style.label')}
      </p>
      <button
        type="button"
        onClick={handleOpenList}
        disabled={disabled}
        className="flex items-center justify-between w-full"
      >
        <span
          className="flex-1 text-[20px] text-spring-neutral-b2 text-center"
          style={{
            fontFamily: selectedFont.fontFamily,
            fontWeight: selectedFont.fontWeight,
          }}
        >
          {selectedFont.name}
        </span>
        <CaretRight className="size-4 text-spring-neutral-b3 shrink-0" />
      </button>
    </div>
  );
};
